import { Component, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvCardComponent } from '../../../../Shared/Components/av-card/av-card.component';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { ShowDeviceFacade } from '../../../../../Facades/Intune/show-device.facade';

@Component({
  selector: 'app-overview',
  standalone: true,
  imports: [COMMON_IMPORTS, AvCardComponent, AvContentContainerComponent],
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
})
export class OverviewComponent {
  private readonly _facade = inject(ShowDeviceFacade);
  private readonly translate = inject(TranslateService);

  readonly device = this._facade.device;

  readonly hardware = computed(() => {
    const d = this.device();
    return {
      manufacturer: d?.intune?.manufacturer || this.translate.instant('shared.general.labels.na'),
      model: d?.intune?.model || this.translate.instant('shared.general.labels.na'),
      serial: d?.intune?.serialNumber || this.translate.instant('shared.general.labels.na'),
      ram: d?.agent?.hardware?.total_ram_gb ? `${d?.agent?.hardware?.total_ram_gb} ${this.translate.instant('shared.general.labels.gb')}` :
        (d?.intune?.physicalMemoryInBytes ? `${Math.round(d?.intune?.physicalMemoryInBytes / (1024 ** 3))} ${this.translate.instant('shared.general.labels.gb')}` : this.translate.instant('shared.general.labels.na'))
    };
  });

  readonly os = computed(() => {
    const d = this.device();
    return {
      name: d?.agent?.os?.caption || d?.intune?.operatingSystem || this.translate.instant('shared.general.labels.na'),
      version: d?.agent?.os?.version || d?.intune?.osVersion || this.translate.instant('shared.general.labels.na'),
      build: d?.agent?.os?.build_number || d?.intune?.hardwareInformation?.osBuildNumber || this.translate.instant('shared.general.labels.na'),
      arch: d?.agent?.os?.os_architecture || d?.intune?.processorArchitecture || this.translate.instant('shared.general.labels.na')
    };
  });

  readonly entra = computed(() => {
    const d = this.device();
    return {
      azureId: d?.intune?.azureADDeviceId || 'Not Registered',
      joinType: d?.intune?.joinType || 'Unknown',
      compliance: d?.intune?.displayCompliance(),
      enrolled: d?.intune?.enrolledDateTime ? new Date(d?.intune?.enrolledDateTime).toLocaleDateString() : this.translate.instant('shared.general.labels.na')
    };
  });

  readonly hardwareFields = computed(() => {
    const h = this.hardware();
    return [
      { label: 'app.device.overview.sections.hardware.fields.manufacturer', value: h.manufacturer },
      { label: 'app.device.overview.sections.hardware.fields.model', value: h.model },
      { label: 'app.device.overview.sections.hardware.fields.serial_number', value: h.serial },
      { label: 'app.device.overview.sections.hardware.fields.total_ram', value: h.ram },
    ];
  });

  readonly ownerFields = computed(() => {
    const d = this.device();
    const owner = d?.owner;
    return [
      { label: 'app.device.overview.sections.owner.fields.name', value: owner?.displayName ?? 'Unknown' },
      { label: 'app.device.overview.sections.owner.fields.principal_name', value: owner?.userPrincipalName },
      { label: 'app.device.overview.sections.owner.fields.email', value: owner?.mail },
      { label: 'app.device.overview.sections.owner.fields.job_title', value: owner?.jobTitle },
    ];
  });

  readonly osFields = computed(() => {
    const o = this.os();
    return [
      { label: 'app.device.overview.sections.os.fields.name', value: o.name },
      { label: 'app.device.overview.sections.os.fields.version', value: o.version },
      { label: 'app.device.overview.sections.os.fields.build_number', value: o.build },
      { label: 'app.device.overview.sections.os.fields.architecture', value: o.arch },
    ];
  });

  readonly entraFields = computed(() => {
    const e = this.entra();
    return [
      { label: 'app.device.overview.sections.entra.fields.join_type', value: e.joinType },
      { label: 'app.device.overview.sections.entra.fields.compliance', value: e.compliance },
      { label: 'app.device.overview.sections.entra.fields.azure_device_id', value: e.azureId },
      { label: 'app.device.overview.sections.entra.fields.enrollment_date', value: e.enrolled },
    ];
  });
}